import React, { useState, useEffect, useCallback } from 'react';
import { styles, colors, radius } from '../../styles/theme';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Kapsam (scope) tipleri — backend _FIYAT_BAND_SCOPE_TIPLERI ile bire bir.
const SCOPE_TIPLERI = [
  { value: 'GLOBAL', label: 'Global (tüm malzemeler)' },
  { value: 'KATEGORI', label: 'Kategori' },
  { value: 'KRITIKLIK', label: 'Kritiklik' },
  { value: 'MALZEME', label: 'Malzeme (tekil)' },
];
// Kritiklik kapalı enum domain (Malzeme.kritiklik) — sabit seçenek olarak kabul edilebilir.
const KRITIKLIK_SECENEKLERI = ['YUKSEK', 'ORTA', 'DUSUK'];

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const emptyBandForm = { scope_tip: 'KATEGORI', scope_deger: '', alt_yuzde: '20', ust_yuzde: '20', min_gecmis: '3', aktif: true };

const FiyatBandKontrolu = () => {
  const toast = useToast();
  const { user } = useAuth();
  const canManage = hasPermission(user?.role, PERMISSIONS.MANAGE_PRICE_BAND, user?.effectivePermissions);

  const [subTab, setSubTab] = useState('ayarlar'); // 'ayarlar' | 'uyarilar'

  // ─── Band ayarları ───────────────────────────────────────────────
  const [bandlar, setBandlar] = useState([]);
  const [bandLoading, setBandLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [malzemeler, setMalzemeler] = useState([]);     // MALZEME scope + KATEGORI distinct kaynağı
  const [addModal, setAddModal] = useState(null);       // band ekleme formu (obj) veya null
  const [editModal, setEditModal] = useState(null);     // { ...band } veya null
  const [malzemeAra, setMalzemeAra] = useState('');     // MALZEME scope arama metni

  // ─── Uyarılar ─────────────────────────────────────────────────────
  const [uyarilar, setUyarilar] = useState([]);
  const [uyariToplam, setUyariToplam] = useState(0);
  const [uyariLoading, setUyariLoading] = useState(false);
  const [uyariOkunanlar, setUyariOkunanlar] = useState(false); // okunanları da göster

  // Malzeme kategorileri DB-driven: malzeme listesindeki distinct kategori değerleri (hardcode YASAK).
  const kategoriler = Array.from(
    new Set(malzemeler.map((m) => (m.kategori || '').trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, 'tr'));

  const fetchBandlar = useCallback(async () => {
    setBandLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/fiyat-band-ayarlari?include_inactive=${includeInactive}&with_usage=true`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json().catch(() => []);
      setBandlar(Array.isArray(data) ? data : []);
    } catch (err) {
      logError('Band ayarları yüklenemedi:', err);
      toast.error('Band ayarları yüklenemedi');
      setBandlar([]);
    }
    setBandLoading(false);
  }, [includeInactive, toast]);

  const fetchMalzemeler = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/malzemeler`, { headers: getAuthHeaders() });
      const data = await res.json().catch(() => []);
      setMalzemeler(Array.isArray(data) ? data : []);
    } catch (err) {
      logError('Malzemeler yüklenemedi:', err);
    }
  }, []);

  const fetchUyarilar = useCallback(async () => {
    setUyariLoading(true);
    try {
      const qs = uyariOkunanlar ? 'limit=200' : 'okundu=false&limit=200';
      const res = await fetch(`${API_URL}/api/fiyat-uyarilari?${qs}`, { headers: getAuthHeaders() });
      const data = await res.json().catch(() => ({ toplam: 0, kayitlar: [] }));
      setUyarilar(Array.isArray(data.kayitlar) ? data.kayitlar : []);
      setUyariToplam(data.toplam || 0);
    } catch (err) {
      logError('Uyarılar yüklenemedi:', err);
      toast.error('Uyarılar yüklenemedi');
      setUyarilar([]);
      setUyariToplam(0);
    }
    setUyariLoading(false);
  }, [uyariOkunanlar, toast]);

  useEffect(() => { fetchBandlar(); }, [fetchBandlar]);
  useEffect(() => { fetchMalzemeler(); }, [fetchMalzemeler]);
  useEffect(() => { if (subTab === 'uyarilar') fetchUyarilar(); }, [subTab, fetchUyarilar]);

  // ─── Band CRUD aksiyonları ────────────────────────────────────────
  const validateBandForm = (f) => {
    const alt = parseFloat(f.alt_yuzde);
    const ust = parseFloat(f.ust_yuzde);
    const minG = parseInt(f.min_gecmis, 10);
    if (!(alt > 0)) { toast.warning('Alt % 0\'dan büyük olmalı'); return null; }
    if (!(ust > 0)) { toast.warning('Üst % 0\'dan büyük olmalı'); return null; }
    if (!(minG >= 1)) { toast.warning('Min. geçmiş en az 1 olmalı'); return null; }
    return { alt, ust, minG };
  };

  const handleCreate = async () => {
    const f = addModal;
    const v = validateBandForm(f);
    if (!v) return;
    if (f.scope_tip !== 'GLOBAL' && !(f.scope_deger || '').trim()) {
      toast.warning('Bu kapsam için kapsam değeri zorunludur'); return;
    }
    try {
      const body = {
        scope_tip: f.scope_tip,
        scope_deger: f.scope_tip === 'GLOBAL' ? null : (f.scope_deger || '').trim(),
        alt_yuzde: v.alt,
        ust_yuzde: v.ust,
        min_gecmis: v.minG,
        aktif: f.aktif !== false,
      };
      const res = await fetch(`${API_URL}/api/fiyat-band-ayarlari`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      toast.success('Band ayarı eklendi');
      setAddModal(null);
      setMalzemeAra('');
      fetchBandlar();
    } catch (err) { toast.error(err.message); }
  };

  const handleUpdate = async () => {
    const f = editModal;
    const v = validateBandForm(f);
    if (!v) return;
    try {
      // scope_tip/scope_deger immutable → PATCH gövdesine KOYMUYORUZ.
      const res = await fetch(`${API_URL}/api/fiyat-band-ayarlari/${f.id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ alt_yuzde: v.alt, ust_yuzde: v.ust, min_gecmis: v.minG, aktif: f.aktif !== false }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      toast.success('Band ayarı güncellendi');
      setEditModal(null);
      fetchBandlar();
    } catch (err) { toast.error(err.message); }
  };

  const handleToggle = async (band) => {
    if (band.scope_tip === 'GLOBAL' && band.aktif) {
      toast.warning('GLOBAL band pasifleştirilemez (varsayılan güvenlik katmanı)'); return;
    }
    try {
      const res = await fetch(`${API_URL}/api/fiyat-band-ayarlari/${band.id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ aktif: !band.aktif }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      fetchBandlar();
    } catch (err) { toast.error(err.message); }
  };

  const handleHardDelete = async (band) => {
    if (band.scope_tip === 'GLOBAL') { toast.warning('GLOBAL band silinemez'); return; }
    const usage = band.usage_count || 0;
    if (usage > 0) { toast.warning(`${usage} uyarıda kullanılıyor — silinemez`); return; }
    const etiket = scopeLabel(band);
    if (!window.confirm(`"${etiket}" band ayarı KALICI olarak silinecek. Geri alınamaz. Emin misin?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/fiyat-band-ayarlari/${band.id}?hard=true`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Silinemedi'); }
      toast.success('Kalıcı olarak silindi');
      fetchBandlar();
    } catch (err) { toast.error(err.message); }
  };

  const handleUyariOkundu = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/fiyat-uyarilari/${id}/okundu`, {
        method: 'PATCH', headers: getAuthHeaders(),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      fetchUyarilar();
    } catch (err) { toast.error(err.message); }
  };

  // ─── Yardımcılar ──────────────────────────────────────────────────
  const scopeLabel = (band) => {
    const tip = (SCOPE_TIPLERI.find((s) => s.value === band.scope_tip) || {}).label || band.scope_tip;
    if (band.scope_tip === 'GLOBAL') return tip;
    if (band.scope_tip === 'MALZEME') {
      const m = malzemeler.find((x) => String(x.id) === String(band.scope_deger));
      return `${tip}: ${m ? `${m.ad}${m.kod ? ` (${m.kod})` : ''}` : band.scope_deger}`;
    }
    return `${tip}: ${band.scope_deger}`;
  };

  const inputStyle = { width: '100%', padding: '6px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box' };
  const labelStyle = { fontSize: 12, color: '#666' };

  // ─── Kapsam değeri akıllı input (Ekle modalı için) ────────────────
  const renderScopeDegerInput = () => {
    const f = addModal;
    if (f.scope_tip === 'GLOBAL') {
      return <input style={{ ...inputStyle, background: '#f5f5f5' }} value="— (tüm malzemeler)" disabled readOnly />;
    }
    if (f.scope_tip === 'KATEGORI') {
      return (
        <select style={inputStyle} value={f.scope_deger} onChange={(e) => setAddModal({ ...f, scope_deger: e.target.value })}>
          <option value="">— Kategori seç —</option>
          {kategoriler.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      );
    }
    if (f.scope_tip === 'KRITIKLIK') {
      return (
        <select style={inputStyle} value={f.scope_deger} onChange={(e) => setAddModal({ ...f, scope_deger: e.target.value })}>
          <option value="">— Kritiklik seç —</option>
          {KRITIKLIK_SECENEKLERI.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      );
    }
    // MALZEME — arama + select
    const filtreli = malzemeAra.trim()
      ? malzemeler.filter((m) =>
          (m.ad || '').toLowerCase().includes(malzemeAra.toLowerCase()) ||
          (m.kod || '').toLowerCase().includes(malzemeAra.toLowerCase()))
      : malzemeler;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <input style={inputStyle} placeholder="Malzeme ara (ad/kod)..." value={malzemeAra} onChange={(e) => setMalzemeAra(e.target.value)} />
        <select style={inputStyle} size={6} value={f.scope_deger} onChange={(e) => setAddModal({ ...f, scope_deger: e.target.value })}>
          {filtreli.slice(0, 200).map((m) => (
            <option key={m.id} value={String(m.id)}>{m.ad}{m.kod ? ` (${m.kod})` : ''}</option>
          ))}
        </select>
      </div>
    );
  };

  const yonBadge = (yon) => {
    const isYuksek = (yon || '').toUpperCase() === 'YUKSEK';
    return (
      <span style={{ background: isYuksek ? '#f8d7da' : '#cce5ff', color: isYuksek ? '#721c24' : '#004085', padding: '2px 8px', borderRadius: radius.lg, fontSize: 11, fontWeight: 600 }}>
        {isYuksek ? '↑ Yüksek' : '↓ Düşük'}
      </span>
    );
  };

  return (
    <div>
      {/* Alt sekmeler */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { key: 'ayarlar', label: `Band Ayarları (${bandlar.length})` },
          { key: 'uyarilar', label: 'Band Dışı Uyarılar' },
        ].map((t) => (
          <button key={t.key} onClick={() => setSubTab(t.key)} style={{ padding: '8px 16px', background: subTab === t.key ? styles.primary : 'white', color: subTab === t.key ? 'white' : '#333', border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontSize: 13 }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ─── BAND AYARLARI ─── */}
      {subTab === 'ayarlar' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
            <p style={{ fontSize: 13, color: '#666', margin: 0, maxWidth: 640 }}>
              Fiyat girişi/fatura onayında geçmiş ortalamaya göre <strong>alt %</strong> altı veya <strong>üst %</strong> üstü sapmalar uyarı üretir.
              En spesifik kapsam kazanır: <strong>Malzeme &gt; Kritiklik &gt; Kategori &gt; Global</strong>.
              <strong> Min. geçmiş</strong>, bandın devreye girmesi için gereken geçmiş fiyat kaydı sayısıdır.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
                Pasifleri göster
              </label>
              {canManage && (
                <button onClick={() => { setMalzemeAra(''); setAddModal({ ...emptyBandForm }); }} style={{ padding: '8px 16px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: 13 }}>+ Band Ekle</button>
              )}
            </div>
          </div>

          {bandLoading ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#999' }}>Yükleniyor...</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Kapsam</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Alt %</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Üst %</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Min. Geçmiş</th>
                  <th style={{ padding: 8 }}>Kullanım</th>
                  <th style={{ padding: 8 }}>Durum</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {bandlar.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#999' }}>Kayıt yok</td></tr>
                )}
                {bandlar.map((b) => {
                  const usage = b.usage_count || 0;
                  const isGlobal = b.scope_tip === 'GLOBAL';
                  return (
                    <tr key={b.id} style={{ borderBottom: `1px solid ${colors.border}`, opacity: b.aktif ? 1 : 0.5 }}>
                      <td style={{ padding: 8 }}>
                        {isGlobal && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#ede7f6', color: '#5e35b1', marginRight: 6 }}>GLOBAL</span>}
                        <strong>{scopeLabel(b)}</strong>
                      </td>
                      <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace' }}>{b.alt_yuzde}%</td>
                      <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace' }}>{b.ust_yuzde}%</td>
                      <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace' }}>{b.min_gecmis}</td>
                      <td style={{ padding: 8, fontSize: 11 }}>{usage > 0 ? <span style={{ background: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: 10 }}>{usage} uyarı</span> : <span style={{ color: '#999' }}>kullanılmamış</span>}</td>
                      <td style={{ padding: 8 }}><span style={{ background: b.aktif ? '#d4edda' : '#f8d7da', color: b.aktif ? '#155724' : '#721c24', padding: '2px 8px', borderRadius: radius.lg, fontSize: 11 }}>{b.aktif ? 'Aktif' : 'Pasif'}</span></td>
                      <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {canManage ? (
                          <>
                            <button onClick={() => setEditModal({ ...b, alt_yuzde: String(b.alt_yuzde), ust_yuzde: String(b.ust_yuzde), min_gecmis: String(b.min_gecmis) })} style={{ padding: '4px 10px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>Düzenle</button>
                            {isGlobal ? (
                              <button disabled title="GLOBAL band pasifleştirilemez" style={{ padding: '4px 10px', background: '#ccc', color: 'white', border: 'none', borderRadius: 4, cursor: 'not-allowed', fontSize: 12, marginRight: 4 }}>Pasifleştir</button>
                            ) : (
                              <button onClick={() => handleToggle(b)} style={{ padding: '4px 10px', background: b.aktif ? '#ffc107' : '#28a745', color: b.aktif ? '#333' : 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>{b.aktif ? 'Pasifleştir' : 'Aktifleştir'}</button>
                            )}
                            <button onClick={() => handleHardDelete(b)} disabled={isGlobal || usage > 0} title={isGlobal ? 'GLOBAL band silinemez' : (usage > 0 ? `${usage} uyarıda kullanılıyor — silinemez` : 'Kalıcı sil')} style={{ padding: '4px 10px', background: (isGlobal || usage > 0) ? '#ccc' : '#dc3545', color: 'white', border: 'none', borderRadius: 4, cursor: (isGlobal || usage > 0) ? 'not-allowed' : 'pointer', fontSize: 12 }}>Sil</button>
                          </>
                        ) : (
                          <span style={{ fontSize: 11, color: '#999' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ─── BAND DIŞI UYARILAR ─── */}
      {subTab === 'uyarilar' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
            <p style={{ fontSize: 13, color: '#666', margin: 0 }}>
              Band dışı fiyat girişleri. <strong>{uyariToplam}</strong> kayıt {uyariOkunanlar ? '(tümü)' : '(okunmamış)'}.
            </p>
            <label style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={uyariOkunanlar} onChange={(e) => setUyariOkunanlar(e.target.checked)} />
              Okunanları da göster
            </label>
          </div>

          {uyariLoading ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#999' }}>Yükleniyor...</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Tarih</th>
                  <th style={{ padding: 8 }}>Malzeme</th>
                  <th style={{ padding: 8 }}>Tedarikçi</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Girilen</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Referans</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Sapma %</th>
                  <th style={{ padding: 8 }}>Yön</th>
                  <th style={{ padding: 8 }}>Fatura No</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {uyarilar.length === 0 && (
                  <tr><td colSpan={9} style={{ padding: 20, textAlign: 'center', color: '#999' }}>Uyarı yok</td></tr>
                )}
                {uyarilar.map((u) => (
                  <tr key={u.id} style={{ borderBottom: `1px solid ${colors.border}`, opacity: u.okundu ? 0.55 : 1 }}>
                    <td style={{ padding: 8, fontSize: 11, color: '#666' }}>{u.created_at ? new Date(u.created_at).toLocaleString('tr-TR') : '—'}</td>
                    <td style={{ padding: 8 }}><strong>{u.malzeme_ad || `#${u.malzeme_id}`}</strong></td>
                    <td style={{ padding: 8 }}>{u.tedarikci_ad || '—'}</td>
                    <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace' }}>{u.girilen_fiyat != null ? Number(u.girilen_fiyat).toFixed(2) : '—'}</td>
                    <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace' }}>{u.referans_fiyat != null ? Number(u.referans_fiyat).toFixed(2) : '—'}</td>
                    <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{u.sapma_yuzde != null ? `${Number(u.sapma_yuzde).toFixed(1)}%` : '—'}</td>
                    <td style={{ padding: 8 }}>{yonBadge(u.yon)}</td>
                    <td style={{ padding: 8, fontSize: 11 }}>{u.fatura_no || '—'}</td>
                    <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {u.okundu ? (
                        <span style={{ fontSize: 11, color: '#28a745' }}>✓ Okundu</span>
                      ) : (
                        <button onClick={() => handleUyariOkundu(u.id)} style={{ padding: '4px 12px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Okundu</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ─── EKLE MODAL ─── */}
      {addModal && (
        <div onClick={() => setAddModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: radius.md, padding: 24, minWidth: 460, maxWidth: 560, boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
            <h3 style={{ marginTop: 0 }}>Yeni Band Ayarı</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Kapsam Tipi*</label>
                <select style={inputStyle} value={addModal.scope_tip} onChange={(e) => setAddModal({ ...addModal, scope_tip: e.target.value, scope_deger: '' })}>
                  {SCOPE_TIPLERI.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Kapsam Değeri{addModal.scope_tip === 'GLOBAL' ? '' : '*'}</label>
                {renderScopeDegerInput()}
              </div>
              <div>
                <label style={labelStyle}>Alt % (düşüş eşiği)*</label>
                <input type="number" min="0" step="0.1" style={inputStyle} value={addModal.alt_yuzde} onChange={(e) => setAddModal({ ...addModal, alt_yuzde: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Üst % (artış eşiği)*</label>
                <input type="number" min="0" step="0.1" style={inputStyle} value={addModal.ust_yuzde} onChange={(e) => setAddModal({ ...addModal, ust_yuzde: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Min. Geçmiş (≥1)*</label>
                <input type="number" min="1" step="1" style={inputStyle} value={addModal.min_gecmis} onChange={(e) => setAddModal({ ...addModal, min_gecmis: e.target.value })} />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={addModal.aktif !== false} onChange={(e) => setAddModal({ ...addModal, aktif: e.target.checked })} />
                  Aktif
                </label>
              </div>
            </div>
            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setAddModal(null)} style={{ padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>Vazgeç</button>
              <button onClick={handleCreate} style={{ padding: '8px 16px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── DÜZENLE MODAL ─── */}
      {editModal && (
        <div onClick={() => setEditModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: radius.md, padding: 24, minWidth: 460, maxWidth: 560, boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
            <h3 style={{ marginTop: 0 }}>Band Ayarı Düzenle</h3>
            <p style={{ fontSize: 12, color: '#666', marginBottom: 16 }}>Not: <strong>Kapsam</strong> (tip + değer) değiştirilemez. Yanlışsa yeni band ekleyip eskisini pasifleştir.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Kapsam (sabit)</label>
                <input style={{ ...inputStyle, background: '#f5f5f5' }} value={scopeLabel(editModal)} disabled readOnly />
              </div>
              <div>
                <label style={labelStyle}>Alt % (düşüş eşiği)*</label>
                <input type="number" min="0" step="0.1" style={inputStyle} value={editModal.alt_yuzde} onChange={(e) => setEditModal({ ...editModal, alt_yuzde: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Üst % (artış eşiği)*</label>
                <input type="number" min="0" step="0.1" style={inputStyle} value={editModal.ust_yuzde} onChange={(e) => setEditModal({ ...editModal, ust_yuzde: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Min. Geçmiş (≥1)*</label>
                <input type="number" min="1" step="1" style={inputStyle} value={editModal.min_gecmis} onChange={(e) => setEditModal({ ...editModal, min_gecmis: e.target.value })} />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                {editModal.scope_tip === 'GLOBAL' ? (
                  <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }} title="GLOBAL band pasifleştirilemez">
                    <input type="checkbox" checked disabled />
                    Aktif (GLOBAL — sabit)
                  </label>
                ) : (
                  <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input type="checkbox" checked={editModal.aktif !== false} onChange={(e) => setEditModal({ ...editModal, aktif: e.target.checked })} />
                    Aktif
                  </label>
                )}
              </div>
            </div>
            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setEditModal(null)} style={{ padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>Vazgeç</button>
              <button onClick={handleUpdate} style={{ padding: '8px 16px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FiyatBandKontrolu;
